import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@mlm/ui';
import { getTopEarners, getTopRecruiters } from '../../lib/api';
import { TenantWideBadge } from './tenant-wide-badge';

/**
 * Top 10 earners by accumulated cash commissions. Always tenant-wide —
 * `ledger_entries` has no branch column, and a member earns from a downline
 * that can span every branch, so "top earners in branch X" isn't a question the
 * data can answer.
 */
export function TopEarnersCard({ filtered = false }: { filtered?: boolean }) {
  const q = useQuery({
    queryKey: ['dashboard', 'top-earners'],
    queryFn: () => getTopEarners(10),
    refetchInterval: 45_000,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          Top earners
          <TenantWideBadge show={filtered} />
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {q.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-7 w-full" />
            ))}
          </div>
        ) : !q.data?.length ? (
          <p className="p-6 text-sm text-muted-foreground">No earnings yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="px-4 py-2 w-8">#</th>
                <th className="px-4 py-2">Member</th>
                <th className="px-4 py-2 text-right">Earned</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((r, i) => (
                <tr key={r.userId} className="border-b last:border-0">
                  <td className="px-4 py-2 text-muted-foreground">{i + 1}</td>
                  <td className="px-4 py-2">{r.fullName ?? r.email}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    ₱{Number(r.lifetimeCashEarned).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

/** Top 10 recruiters by active direct referrals. Tenant-wide — see above. */
export function TopRecruitersCard({ filtered = false }: { filtered?: boolean }) {
  const q = useQuery({
    queryKey: ['dashboard', 'top-recruiters'],
    queryFn: () => getTopRecruiters(10),
    refetchInterval: 45_000,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          Top recruiters
          <TenantWideBadge show={filtered} />
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {q.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-7 w-full" />
            ))}
          </div>
        ) : !q.data?.length ? (
          <p className="p-6 text-sm text-muted-foreground">No referrals yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="px-4 py-2 w-8">#</th>
                <th className="px-4 py-2">Member</th>
                <th className="px-4 py-2 text-right">Directs</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((r, i) => (
                <tr key={r.userId} className="border-b last:border-0">
                  <td className="px-4 py-2 text-muted-foreground">{i + 1}</td>
                  <td className="px-4 py-2">{r.fullName ?? r.email}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.directs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
